import React, { useMemo } from 'react';
import { MenuItems, type MenuItemsProps } from '@headlessui/react';
import { twMerge } from 'tailwind-merge';
import { useAppSelector } from 'flow/hooks/useRedux';
import useAppContext from 'flow/hooks/useAppContext';
import {
    selectExtensions,
    selectOwnExtensionKey,
} from 'flow/selectors/ExtensionSelectors';
import { selectMembers } from 'flow/selectors/TicketsSelectors';
import { TicketKey } from 'flow/types/EntityKeys';
import { getTicketAssigneeOptionsFromMembers } from 'flow/utils/ticket-utils';

import AssigneePickerItem from './AssigneePickerItem';
import { AssigneeKey } from './useAssigneeLabel';

interface Props {
    ticketKey: TicketKey;
    onSelect: (key: AssigneeKey) => void;
    // When set, the menu is portaled and positioned against the button via
    // floating-ui, so it can escape an `overflow-hidden` ancestor (e.g. a list
    // row). When omitted, it renders in place, absolutely positioned.
    anchor?: MenuItemsProps['anchor'];
}

const AssigneePickerMenu = ({ ticketKey, onSelect, anchor }: Props) => {
    const { i18next } = useAppContext();
    const ownExtensionKey = useAppSelector(selectOwnExtensionKey);
    const members = useAppSelector((state) => selectMembers(state, ticketKey));
    const extensions = useAppSelector(selectExtensions);

    const sortedKeys = useMemo(
        () =>
            getTicketAssigneeOptionsFromMembers(
                i18next,
                ownExtensionKey,
                members,
                extensions,
            ).map((option) => option.key),
        [i18next, ownExtensionKey, members, extensions],
    );

    return (
        <MenuItems
            anchor={anchor}
            className={twMerge(
                'scrollbar shadow-base scrollbar-gutter-stable z-20 mt-2 max-h-72 w-64 rounded-md bg-white py-2 ps-2 outline-gray-100 focus:outline dark:bg-gray-300',
                // When anchored, headless applies an inline max-height from the
                // available viewport space, overriding max-h-72 — cap it to the
                // same 18rem (max-h-72) via the anchor's own variable.
                anchor
                    ? '[--anchor-max-height:18rem]'
                    : 'origin-top-end absolute end-0',
            )}
        >
            {sortedKeys.map((key) => (
                <AssigneePickerItem
                    key={`api-${key}`}
                    extensionKey={key}
                    onSelect={onSelect}
                />
            ))}
        </MenuItems>
    );
};

export default React.memo(AssigneePickerMenu);
